/* This FaqTemplate requires Tailwind CSS v2.0+ */
import { Disclosure } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/outline';
import useLocale from 'next-translate/useTranslation';

import clsxm from '@/lib/clsxm';

import MarkdownBlock from './Chat/MarkdownBlock';

export interface FaqItem {
  question: string;
  answer: string;
}

interface FaqTemplateProps {
  // Only the current template's entries, selected in getStaticProps — the
  // full templateFaqs corpus (~630KB of strings) must never be imported
  // client-side, or it ships in the page's JS chunk for every visitor. Same
  // for CLINICAL_PRESET_SLUGS (it would drag the whole presets module in);
  // the clinical check happens server-side and arrives as a boolean.
  faqs?: FaqItem[];
  isClinical?: boolean;
}

export default function FaqTemplate({
  faqs: baseFaqs = [],
  isClinical = false,
}: FaqTemplateProps) {
  const { t, lang } = useLocale('common');
  // Clinical tools always surface a HIPAA / BAA answer first — clinicians ask
  // about this before anything else.
  const faqs = isClinical
    ? [
        {
          question: 'Is this tool HIPAA-compliant? Do you sign a BAA?',
          answer:
            'Yes. Easy-Peasy.AI supports HIPAA-compliant use for clinical documentation and offers a Business Associate Agreement (BAA) for clinical accounts. You can review, sign, and download the BAA in your account settings: [Manage your BAA](/settings/baa).',
        },
        ...baseFaqs,
      ]
    : baseFaqs;

  if (!faqs.length || lang !== 'en') return null;

  return (
    <div className='bg-gray-50 dark:bg-slate-900'>
      <div className='mx-auto max-w-7xl py-12 px-4 sm:py-16 sm:px-6 lg:px-8'>
        <div
          className='mx-auto max-w-3xl divide-y-2 divide-gray-200 dark:divide-slate-600'
          itemScope
          itemType='https://schema.org/FAQPage'
        >
          <h2 className='text-center text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl dark:text-slate-200'>
            {t('Frequently asked questions')}
          </h2>
          <dl className='mt-6 space-y-6 divide-y divide-gray-200 dark:divide-slate-600'>
            {faqs.map((faq) => (
              <Disclosure
                as='div'
                key={faq.question}
                className='pt-6'
                itemScope
                itemProp='mainEntity'
                itemType='https://schema.org/Question'
              >
                {({ open }) => (
                  <>
                    <dt className='text-lg'>
                      <Disclosure.Button className='flex w-full items-start justify-between text-left text-gray-400 dark:text-slate-400'>
                        <span
                          className='font-medium text-gray-900 dark:text-slate-200'
                          itemProp='name'
                        >
                          {faq.question}
                        </span>
                        <span className='ml-6 flex h-7 items-center'>
                          <ChevronDownIcon
                            className={clsxm(
                              open ? '-rotate-180' : 'rotate-0',
                              'h-6 w-6 transform',
                            )}
                            aria-hidden='true'
                          />
                        </span>
                      </Disclosure.Button>
                    </dt>
                    <Disclosure.Panel
                      as='dd'
                      className='mt-2 pr-12'
                      itemScope
                      itemProp='acceptedAnswer'
                      unmount={false}
                      itemType='https://schema.org/Answer'
                    >
                      <div
                        className='text-base text-gray-500 dark:text-slate-400'
                        itemProp='text'
                      >
                        <MarkdownBlock
                          message={faq.answer.replace(/\n/g, '  \n')}
                        />
                      </div>
                    </Disclosure.Panel>
                  </>
                )}
              </Disclosure>
            ))}
          </dl>
        </div>
      </div>
    </div>
  );
}
